import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Info,
  Folder,
  ArrowUp,
  ArrowDown,
  Minus,
  SlidersHorizontal,
  ListFilter,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Skeleton } from "../common/Skeleton";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Card } from "../ui/card";
import { testcaseService } from "../../services/testcaseService";
import sectionService from "../../services/sectionService";
import toast from "react-hot-toast";
import MuiSelect from "../MuiSelect";
const SelectTestCasesModal = ({
  open,
  onClose,
  projectId,
  onSave,
  selectedTestCases = [],
  mode = "specific",
}) => {
  const [testCases, setTestCases] = useState([]);
  const [allTestCases, setAllTestCases] = useState([]); // Store all test cases
  const [sections, setSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState(
    new Set(selectedTestCases.map((tc) => tc.test_case_id || tc)),
  );
  const [loading, setLoading] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(mode === "filter"); // Expanded for filter mode, collapsed for specific mode
  const [filters, setFilters] = useState({
    key: "",
    name: "",
    suite: "",
    type: "",
    references: "",
    automation_id: "",
    external_id: "",
    status: "",
    priority: "",
    automation_type: "",
    automation_status: "",
    label: "",
    owner: "",
    test_suites: "",
    baseline: "",
    branch: "",
  });
  const [filterLogic, setFilterLogic] = useState("all"); // 'all' or 'any'
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFolders, setExpandedFolders] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Load sections
  const loadSections = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await sectionService.getSectionTree(projectId);
      if (response.success) {
        setSections(response.data?.sections || response.data?.folders || []);
      }
    } catch (error) {
      console.error("Error loading sections:", error);
    }
  }, [projectId]);

  // Load all test cases using /testcase/list API with pagination
  const loadAllTestCases = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const allCases = [];
      let page = 1;
      const limit = 100; // Maximum allowed limit
      let hasMore = true;
      let totalCount = 0;

      // Fetch all test cases in batches of 100
      while (hasMore) {
        const response = await testcaseService.getTestCases(projectId, {
          page,
          limit,
          sort_by: "created_at",
          sort_order: "ASC",
        });

        if (response.success) {
          const pageCases = response.data?.test_cases || response.data || [];
          allCases.push(...pageCases);

          // Get total count from pagination info (only on first page)
          if (page === 1) {
            if (response.data?.pagination?.total) {
              totalCount = response.data.pagination.total;
            } else if (response.pagination?.total) {
              totalCount = response.pagination.total;
            }
          }

          // Check if we've fetched all test cases
          // Stop if: no more cases returned, or we've reached the total count, or totalCount is 0 and we got an empty page
          if (
            pageCases.length === 0 ||
            (totalCount > 0 && allCases.length >= totalCount)
          ) {
            hasMore = false;
          } else if (pageCases.length < limit) {
            // If we got fewer cases than the limit, we've reached the end
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
          console.error(
            "Error fetching test cases:",
            response.message || response.error,
          );
        }
      }

      // Map to expected format
      const mappedCases = allCases.map((tc) => {
        // Calculate estimated time from custom_fields
        // custom_fields is a JSONB array, need to extract estimated_time
        let estimatedTime = 0;
        if (tc.custom_fields) {
          try {
            // If custom_fields is a string, parse it
            const customFields =
              typeof tc.custom_fields === "string"
                ? JSON.parse(tc.custom_fields)
                : tc.custom_fields;

            // If it's an array, find the estimated_time field
            if (Array.isArray(customFields)) {
              const estimatedTimeField = customFields.find(
                (field) =>
                  field.key === "estimated_time" ||
                  field.name === "estimated_time",
              );
              if (estimatedTimeField) {
                estimatedTime =
                  parseFloat(estimatedTimeField.value || estimatedTimeField) ||
                  0;
              }
            } else if (customFields.estimated_time) {
              // If it's an object, access directly
              estimatedTime = parseFloat(customFields.estimated_time) || 0;
            }
          } catch (e) {
            console.warn("Error parsing custom_fields:", e);
          }
        }

        return {
          test_case_id: tc.test_case_id,
          title: tc.title,
          key: tc.test_case_id,
          type: tc.automation_status || "Functional",
          estimated_time: estimatedTime,
          section_id: tc.section_id || tc.folder_id, // Support both for backward compatibility
          folder_id: tc.section_id || tc.folder_id, // Keep for API compatibility
          folder_path:
            tc.section_path || tc.folder_path || tc.folder_name || "",
          scenario_id: tc.scenario_id,
          aliases: tc.aliases || [],
          priority: tc.priority || "medium",
          owner: tc.owner || null,
          owner_name: tc.owner_name || null,
          tags: tc.tags || [],
        };
      });

      setAllTestCases(mappedCases);
      setTestCases(mappedCases); // Show all by default

      if (mappedCases.length > 0) {
        toast.success(`Loaded ${mappedCases.length} test cases`);
      }
    } catch (error) {
      console.error("Error loading test cases:", error);
      toast.error("Failed to load test cases");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleApplyFilters = () => {};

  // Filter test cases based on selected section and filters
  const filterTestCases = useCallback(() => {
    let filtered = [...allTestCases];

    // Filter by selected section
    if (selectedSection) {
      filtered = filtered.filter(
        (tc) => (tc.section_id || tc.folder_id) === selectedSection,
      );
    }

    // Apply other filters
    if (filters.key) {
      filtered = filtered.filter(
        (tc) => tc.test_case_id === filters.key || tc.key === filters.key,
      );
    }
    if (filters.name) {
      filtered = filtered.filter((tc) =>
        tc.title?.toLowerCase().includes(filters.name.toLowerCase()),
      );
    }
    if (filters.suite && !selectedSection) {
      filtered = filtered.filter(
        (tc) => (tc.section_id || tc.folder_id) === filters.suite,
      );
    }
    if (filters.type) {
      filtered = filtered.filter((tc) => tc.type === filters.type);
    }
    if (filters.references) {
      filtered = filtered.filter(
        (tc) =>
          tc.scenario_id
            ?.toLowerCase()
            .includes(filters.references.toLowerCase()) ||
          tc.aliases?.some((alias) =>
            alias.toLowerCase().includes(filters.references.toLowerCase()),
          ),
      );
    }

    setTestCases(filtered);
  }, [allTestCases, selectedSection, filters]);

  useEffect(() => {
    if (open && projectId) {
      loadSections();
      loadAllTestCases();
    }
  }, [open, projectId, loadSections, loadAllTestCases]);

  useEffect(() => {
    filterTestCases();
  }, [filterTestCases]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = async () => {
    if (mode === "filter") {
      // For filter mode, fetch test cases from API with filters using /testcase/list with pagination
      setLoading(true);
      try {
        const baseFilterParams = {
          sort_by: "created_at",
          sort_order: "ASC",
        };

        // Map filter fields to API parameters
        if (filters.key) baseFilterParams.scenario_id = filters.key;
        if (filters.name) baseFilterParams.search_term = filters.name;
        if (filters.suite) baseFilterParams.section_id = filters.suite; // API may still accept folder_id for backward compatibility
        if (filters.type) baseFilterParams.automation_status = filters.type;
        if (filters.references)
          baseFilterParams.search_term = filters.references; // Can combine with name
        if (filters.automation_id)
          baseFilterParams.search_term = filters.automation_id; // Can combine
        if (filters.external_id)
          baseFilterParams.search_term = filters.external_id; // Can combine

        const allCases = [];
        let page = 1;
        const limit = 100; // Maximum allowed limit
        let hasMore = true;
        let totalCount = 0;

        // Fetch all matching test cases in batches of 100
        while (hasMore) {
          const filterParams = {
            ...baseFilterParams,
            page,
            limit,
          };

          const response = await testcaseService.getTestCases(
            projectId,
            filterParams,
          );
          if (response.success) {
            const pageCases = response.data?.test_cases || response.data || [];
            allCases.push(...pageCases);

            // Get total count from pagination info (only on first page)
            if (page === 1) {
              if (response.data?.pagination?.total) {
                totalCount = response.data.pagination.total;
              } else if (response.pagination?.total) {
                totalCount = response.pagination.total;
              }
            }

            // Check if we've fetched all test cases
            // Stop if: no more cases returned, or we've reached the total count, or totalCount is 0 and we got an empty page
            if (
              pageCases.length === 0 ||
              (totalCount > 0 && allCases.length >= totalCount)
            ) {
              hasMore = false;
            } else if (pageCases.length < limit) {
              // If we got fewer cases than the limit, we've reached the end
              hasMore = false;
            } else {
              page++;
            }
          } else {
            hasMore = false;
            console.error(
              "Error fetching test cases:",
              response.message || response.error,
            );
          }
        }

        const mappedCases = allCases.map((tc) => {
          // Calculate estimated time from custom_fields
          let estimatedTime = 0;
          if (tc.custom_fields) {
            try {
              const customFields =
                typeof tc.custom_fields === "string"
                  ? JSON.parse(tc.custom_fields)
                  : tc.custom_fields;

              if (Array.isArray(customFields)) {
                const estimatedTimeField = customFields.find(
                  (field) =>
                    field.key === "estimated_time" ||
                    field.name === "estimated_time",
                );
                if (estimatedTimeField) {
                  estimatedTime =
                    parseFloat(
                      estimatedTimeField.value || estimatedTimeField,
                    ) || 0;
                }
              } else if (customFields.estimated_time) {
                estimatedTime = parseFloat(customFields.estimated_time) || 0;
              }
            } catch (e) {
              console.warn("Error parsing custom_fields:", e);
            }
          }
          return {
            test_case_id: tc.test_case_id,
            title: tc.title,
            key: tc.test_case_id,
            type: tc.automation_status || "Functional",
            estimated_time: estimatedTime,
            section_id: tc.section_id || tc.folder_id, // Support both for backward compatibility
            folder_id: tc.section_id || tc.folder_id, // Keep for API compatibility
            folder_path:
              tc.section_path || tc.folder_path || tc.folder_name || "",
            scenario_id: tc.scenario_id,
            priority: tc.priority || "medium",
            owner: tc.owner || null,
            owner_name: tc.owner_name || null,
            tags: tc.tags || [],
          };
        });
        setTestCases(mappedCases);
        setAllTestCases(mappedCases);

        if (mappedCases.length > 0) {
          toast.success(
            `Found ${mappedCases.length} test cases matching filters`,
          );
        } else {
          toast("No test cases found matching the filters");
        }
      } catch (error) {
        console.error("Error searching test cases:", error);
        toast.error("Failed to search test cases");
      } finally {
        setLoading(false);
      }
    } else {
      // For specific mode, use local filtering
      filterTestCases();
    }
  };

  const handleReset = async () => {
    setFilters({
      key: "",
      name: "",
      suite: "",
      type: "",
      references: "",
      automation_id: "",
      external_id: "",
    });
    setSelectedSection(null);
    if (mode === "filter") {
      // Reload all test cases
      await loadAllTestCases();
    } else {
      setTestCases(allTestCases);
    }
  };

  const handleTestCaseToggle = (testCaseId) => {
    setSelectedTestCaseIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(testCaseId)) {
        newSet.delete(testCaseId);
      } else {
        newSet.add(testCaseId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedTestCaseIds.size === testCases.length) {
      setSelectedTestCaseIds(new Set());
    } else {
      setSelectedTestCaseIds(new Set(testCases.map((tc) => tc.test_case_id)));
    }
  };

  const handleSectionSelect = (sectionId) => {
    if (sectionId === null) {
      setExpandedFolders((prev) => !prev);
    }
    const newSectionId = sectionId === selectedSection ? null : sectionId;
    setSelectedSection(newSectionId);
    setFilters((prev) => ({ ...prev, suite: newSectionId || "" }));
  };

  const toggleSection = (sectionId, e) => {
    e.stopPropagation();
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    if (mode === "filter") {
      // For filter mode, save the filter criteria
      // Clean up empty filter values
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) => value !== "" && value !== null,
        ),
      );
      onSave({ filter_criteria: cleanFilters, test_case_ids: [] });
    } else {
      // For specific mode, save the selected test cases
      const selected = testCases.filter((tc) =>
        selectedTestCaseIds.has(tc.test_case_id),
      );
      onSave(selected);
    }
    onClose();
  };

  // Calculate totals
  const selectedTestCasesList = testCases.filter((tc) =>
    selectedTestCaseIds.has(tc.test_case_id),
  );
  const totalEstimatedHours = selectedTestCasesList.reduce(
    (sum, tc) => sum + (parseFloat(tc.estimated_time) || 0),
    0,
  );

  // For filter mode, show filtered count; for specific mode, show selected count
  const displayCount =
    mode === "filter" ? testCases.length : selectedTestCaseIds.size;
  const displayHours =
    mode === "filter"
      ? testCases.reduce(
          (sum, tc) => sum + (parseFloat(tc.estimated_time) || 0),
          0,
        )
      : totalEstimatedHours;

  // Calculate test case counts per section
  const getSectionTestCaseCount = (sectionId) => {
    if (!sectionId) return allTestCases.length;
    const sectionCases = allTestCases.filter(
      (tc) => (tc.section_id || tc.folder_id) === sectionId,
    );
    const selectedInSection = sectionCases.filter((tc) =>
      selectedTestCaseIds.has(tc.test_case_id),
    ).length;
    return { total: sectionCases.length, selected: selectedInSection };
  };

  // Recursively count test cases in section and children
  const getSectionTotalCount = (section) => {
    const sectionId = section.section_id || section.folder_id;
    const directCount = getSectionTestCaseCount(sectionId);
    let childrenCount = { total: 0, selected: 0 };
    if (section.children && section.children.length > 0) {
      section.children.forEach((child) => {
        const childCount = getSectionTotalCount(child);
        childrenCount.total += childCount.total;
        childrenCount.selected += childCount.selected;
      });
    }
    return {
      total: directCount.total + childrenCount.total,
      selected: directCount.selected + childrenCount.selected,
    };
  };

  // Build section tree structure from flat list
  const buildSectionTree = (sectionList) => {
    const sectionMap = new Map();
    const rootSections = [];

    // Create map of all sections (support both section_id and folder_id for backward compatibility)
    sectionList.forEach((section) => {
      const sectionId = section.section_id || section.folder_id;
      const parentSectionId =
        section.parent_section_id !== undefined
          ? section.parent_section_id
          : section.parent_folder_id;
      sectionMap.set(sectionId, {
        ...section,
        section_id: sectionId,
        parent_section_id: parentSectionId,
        children: [],
      });
    });

    // Build hierarchy
    sectionList.forEach((section) => {
      const sectionId = section.section_id || section.folder_id;
      const parentSectionId =
        section.parent_section_id !== undefined
          ? section.parent_section_id
          : section.parent_folder_id;
      const sectionNode = sectionMap.get(sectionId);
      if (parentSectionId) {
        const parent = sectionMap.get(parentSectionId);
        if (parent) {
          parent.children.push(sectionNode);
        } else {
          rootSections.push(sectionNode);
        }
      } else {
        rootSections.push(sectionNode);
      }
    });

    return rootSections;
  };

  const sectionTree = buildSectionTree(sections);

  // Render section tree recursively with expand/collapse and counts
  const renderSectionTree = (sectionNodes, level = 0) => {
    return sectionNodes.map((section) => {
      const sectionId = section.section_id || section.folder_id;
      const hasChildren = section.children && section.children.length > 0;
      const isExpanded = expandedSections.has(sectionId);
      const isSelected = selectedSection === sectionId;
      const counts = getSectionTotalCount(section);
      const displayName = section.name || section.path || "Unnamed";
      const truncatedName =
        displayName.length > 15
          ? displayName.substring(0, 12) + "..."
          : displayName;

      return (
        <div key={sectionId}>
          <div
            onClick={() => handleSectionSelect(sectionId)}
            className={`p-2 cursor-pointer rounded transition-colors flex items-center gap-2 ${
              isSelected ? "bg-primary-50" : "hover:bg-bg-hover"
            }`}
            style={{ marginLeft: `${level * 20}px` }}
          >
            {hasChildren ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={(e) => toggleSection(sectionId, e)}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-[#3B82F6FF]" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[#3B82F6FF]" />
                )}
              </Button>
            ) : (
              <div className="w-5" />
            )}
            <Folder className="w-4 h-4 text-[#3B82F6FF]" />
            <span className="flex-1 text-text-primary text-sm">
              {truncatedName}
            </span>
            <span className="text-xs text-text-secondary">
              {counts.selected > 0 ? `${counts.selected}/` : ""}
              {counts.total}
            </span>
          </div>
          {hasChildren &&
            isExpanded &&
            renderSectionTree(section.children, level + 1)}
        </div>
      );
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[90vw] max-h-[90vh] overflow-y-auto px-4">
        <DialogHeader>
          <div className="flex items-center justify-between my-2">
            <DialogTitle>Add Test Cases to Test Run</DialogTitle>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-red transition-colors whitespace-nowrap flex-shrink-0"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={mode === "specific" && selectedTestCaseIds.size === 0}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-brown transition-colors whitespace-nowrap flex-shrink-0"
              >
                Save
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-1 text-left text-sm text-text-secondary">
            <span>
              Selected: {displayCount} Test{" "}
              {displayCount === 1 ? "Case" : "Cases"}
            </span>
            <span>•</span>
            <span>Estimated Hours: {displayHours.toFixed(2)} hrs</span>
          </div>
        </DialogHeader>

        <div className="space-y-2">
          {/* Filters Section */}
          <div>
            {mode === "filter" && (
              <>
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center gap-2 mb-2">
                    <ListFilter className="w-5 h-5 text-[#1C1918FF]" />
                    <Label className="text-lg font-medium">Filter List</Label>
                  </div>
                  <div className="flex items-end gap-2 mb-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-[#F0F5FEFF] text-[#2E70F5FF] hover:bg-[#E4EDFEFF] hover:text-[#2E70F5FF] min-w-28"
                      onClick={handleApplyFilters}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      size="sm"
                      className="bg-[#F0F5FEFF] text-[#2E70F5FF] hover:bg-[#E4EDFEFF] hover:text-[#2E70F5FF] min-w-28"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 border rounded-lg bg-white">
                  <div>
                    <Label>Select specific folder</Label>
                    <Select
                      value={filters.suite || "all"}
                      onValueChange={(value) =>
                        handleFilterChange(
                          "suite",
                          value === "all" ? "" : value,
                        )
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="0 Folder selected" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Folders</SelectItem>
                        {sections.map((section) => {
                          const sectionId =
                            section.section_id || section.folder_id;
                          return (
                            <SelectItem key={sectionId} value={sectionId}>
                              {section.path || section.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="filter-suite">Suite</Label>
                    <Select
                      value={filters.suite || "all"}
                      onValueChange={(value) =>
                        handleFilterChange(
                          "suite",
                          value === "all" ? "" : value,
                        )
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="All Suites" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Suites</SelectItem>
                        {sections.map((section) => {
                          const sectionId =
                            section.section_id || section.folder_id;
                          return (
                            <SelectItem key={sectionId} value={sectionId}>
                              {section.path || section.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="">Owner</Label>
                    <Select
                      value={filters.owner || "all"}
                      onValueChange={(value) =>
                        handleFilterChange(
                          "owner",
                          value === "all" ? "" : value,
                        )
                      }
                    >
                      <SelectTrigger className=" mt-1">
                        <SelectValue placeholder="Select Options" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {/* TODO: Load owners from API */}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="filter-type">Type</Label>
                    <Select
                      value={filters.type || "all"}
                      onValueChange={(value) =>
                        handleFilterChange("type", value === "all" ? "" : value)
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="not_automated">
                          Not Automated
                        </SelectItem>
                        <SelectItem value="automated">Automated</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Automation Status</Label>
                    <Select
                      value={filters.automation_status || "all"}
                      onValueChange={(value) =>
                        handleFilterChange(
                          "automation_status",
                          value === "all" ? "" : value,
                        )
                      }
                    >
                      <SelectTrigger className=" mt-1">
                        <SelectValue placeholder="Select Options" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="ready">Ready</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Automation Type</Label>
                    <Select
                      value={filters.automation_type || "all"}
                      onValueChange={(value) =>
                        handleFilterChange(
                          "automation_type",
                          value === "all" ? "" : value,
                        )
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select Options" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="not_automated">
                          Not Automated
                        </SelectItem>
                        <SelectItem value="automated">Automated</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Label</Label>
                    <Select
                      value={filters.label || "all"}
                      onValueChange={(value) =>
                        handleFilterChange(
                          "label",
                          value === "all" ? "" : value,
                        )
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select Options" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {/* TODO: Load labels from API */}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="shrink-0 flex min-h-10 flex-row flex-wrap items-center gap-x-6 gap-y-1 bg-background px-3 mt-4">
                    <div className="flex h-8 min-h-8 items-center gap-2.5">
                      <input
                        type="radio"
                        id="match-all"
                        name="filter-logic"
                        value="all"
                        checked={filterLogic === "all"}
                        onChange={(e) => setFilterLogic(e.target.value)}
                        className="h-5 w-5 shrink-0 cursor-pointer"
                        style={{ accentColor: "#a17561ff" }}
                      />
                      <Label
                        htmlFor="match-all"
                        className="cursor-pointer text-base font-normal leading-none text-text-primary"
                      >
                        Match all filter
                      </Label>
                    </div>
                    <div className="flex h-10 min-h-10 items-center gap-2.5">
                      <input
                        type="radio"
                        id="match-any"
                        name="filter-logic"
                        value="any"
                        checked={filterLogic === "any"}
                        onChange={(e) => setFilterLogic(e.target.value)}
                        className="h-5 w-5 shrink-0 cursor-pointer"
                        style={{ accentColor: "#a17561ff" }}
                      />
                      <Label
                        htmlFor="match-any"
                        className="cursor-pointer text-base font-normal leading-none text-text-primary"
                      >
                        Match any one filters
                      </Label>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Main Content */}
          {mode === "filter" ? (
            /* Filter Mode - Only show table (no section tree, no checkboxes) */
            <Card>
              <div className="p-4 border-b">
                <h3 className="font-semibold text-text-primary">
                  All Test Cases
                </h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>NAME</TableHead>
                      <TableHead>KEY</TableHead>
                      <TableHead>TYPE</TableHead>
                      <TableHead>ESTIMATED TIME</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      [0, 1, 2].map((i) => (
                        <TableRow key={`sk-a-${i}`}>
                          <TableCell>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-16" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-20" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-14" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : testCases.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-text-secondary"
                        >
                          No test cases found. Apply filters and click Search.
                        </TableCell>
                      </TableRow>
                    ) : (
                      testCases.map((testCase) => (
                        <TableRow
                          key={testCase.test_case_id}
                          className="hover:bg-bg-hover"
                        >
                          <TableCell className="text-text-primary">
                            {testCase.folder_path ? (
                              <div>
                                <span className="font-medium">
                                  {testCase.folder_path} &gt; {testCase.title}
                                </span>
                              </div>
                            ) : (
                              <span>{testCase.title}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-text-primary">
                            {testCase.key || testCase.test_case_id}
                          </TableCell>
                          <TableCell className="text-text-primary">
                            {testCase.type || "Functional"}
                          </TableCell>
                          <TableCell className="text-text-primary">
                            {testCase.estimated_time || 0}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ) : (
            /* Specific Mode - Show three-column layout: folder tree, test cases table, filters */
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:min-h-0 md:h-[min(600px,calc(100vh-12rem))] md:items-stretch">
              {/* Left Panel - Folder Tree */}
              <Card className="md:col-span-3 p-4 flex flex-col min-h-0 h-full max-h-[70vh] md:max-h-none overflow-hidden">
                <div className="shrink-0 flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-text-primary">Folders</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedTestCaseIds(new Set());
                      setSelectedSection(null);
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <div
                    onClick={() => handleSectionSelect(null)}
                    className={`p-2 cursor-pointer rounded transition-colors flex items-center gap-2 mb-1 ${
                      selectedSection === null
                        ? "bg-primary-50"
                        : "hover:bg-bg-hover"
                    }`}
                  >
                    {expandedFolders ? (
                      <ChevronDown className="w-4 h-4 text-[#3B82F6FF]" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-[#3B82F6FF]" />
                    )}
                    <Folder className="w-4 h-4 text-[#3B82F6FF]" />
                    <span className="flex-1 text-text-primary text-sm">
                      All Test Cases
                    </span>
                    <span className="text-xs text-text-secondary">
                      {selectedTestCaseIds.size}/{allTestCases.length}
                    </span>
                  </div>
                  {expandedFolders && renderSectionTree(sectionTree)}
                </div>
              </Card>

              {/* Middle Panel - Test Cases Table (wider when right filters are hidden) */}
              <div
                className={`flex flex-col min-h-0 h-full ${
                  showFilters ? "md:col-span-6" : "md:col-span-9"
                }`}
              >
                <Card className="flex flex-col h-full min-h-0 overflow-hidden">
                  <div className="shrink-0 px-4 pt-4 pb-4 border-b flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <Input
                        leftIcon={<Search className="w-4 h-4" />}
                        placeholder="Search by Test Case ID or Title"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <button
                      type="button"
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-background ${
                        showFilters
                          ? "border-primary-500 ring-1 ring-primary-500/30"
                          : "border-border-primary"
                      }`}
                      aria-label={
                        showFilters
                          ? "Hide filters panel"
                          : "Show filters panel"
                      }
                      aria-pressed={showFilters}
                      onClick={() => setShowFilters((prev) => !prev)}
                    >
                      <SlidersHorizontal className="h-4 w-4 text-[#1C1918FF]" />
                    </button>
                  </div>
                  <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto px-4 pb-4 box-border">
                    <Table className="w-full min-w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={
                                testCases.length > 0 &&
                                selectedTestCaseIds.size === testCases.length
                              }
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          <TableHead>ID</TableHead>
                          <TableHead>TITLE</TableHead>
                          <TableHead>PRIORITY</TableHead>
                          <TableHead>OWNER</TableHead>
                          <TableHead>Label</TableHead>
                          <TableHead>Estimated Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading ? (
                          [0, 1, 2].map((i) => (
                            <TableRow key={`sk-b-${i}`}>
                              <TableCell>
                                <Skeleton className="h-4 w-10" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-24" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-full max-w-[12rem]" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-20" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-14" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-12" />
                              </TableCell>
                            </TableRow>
                          ))
                        ) : testCases.filter((tc) => {
                            if (!searchQuery) return true;
                            const query = searchQuery.toLowerCase();
                            return (
                              tc.test_case_id?.toLowerCase().includes(query) ||
                              tc.title?.toLowerCase().includes(query)
                            );
                          }).length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="text-center text-text-secondary"
                            >
                              No test cases found
                            </TableCell>
                          </TableRow>
                        ) : (
                          testCases
                            .filter((tc) => {
                              if (!searchQuery) return true;
                              const query = searchQuery.toLowerCase();
                              return (
                                tc.test_case_id
                                  ?.toLowerCase()
                                  .includes(query) ||
                                tc.title?.toLowerCase().includes(query)
                              );
                            })
                            .map((testCase) => (
                              <TableRow
                                key={testCase.test_case_id}
                                className={`hover:bg-bg-hover ${
                                  selectedTestCaseIds.has(testCase.test_case_id)
                                    ? "bg-primary-50"
                                    : ""
                                }`}
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={selectedTestCaseIds.has(
                                      testCase.test_case_id,
                                    )}
                                    onCheckedChange={() =>
                                      handleTestCaseToggle(
                                        testCase.test_case_id,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell className="text-text-primary text-sm">
                                  {testCase.test_case_id || testCase.key}
                                </TableCell>
                                <TableCell className="text-text-primary text-sm">
                                  <div
                                    className="max-w-[300px] truncate"
                                    title={testCase.title}
                                  >
                                    {testCase.title}
                                  </div>
                                </TableCell>
                                <TableCell className="flex flex-row items-center gap-1.5">
                                  {testCase.priority === "high" ? (
                                    <ArrowUp className="w-4 h-4 text-red-500" />
                                  ) : testCase.priority === "medium" ? (
                                    <Minus className="w-4 h-4 text-[#22C55EFF]" />
                                  ) : testCase.priority === "low" ? (
                                    <ArrowDown className="w-4 h-4 text-[#3B82F6FF]" />
                                  ) : (
                                    <ArrowUp className="w-4 h-4 text-red-500" />
                                  )}
                                  <span className={`text-xs'}`}>
                                    {testCase.priority
                                      ? testCase.priority
                                          .charAt(0)
                                          .toUpperCase() +
                                        testCase.priority.slice(1)
                                      : "Medium"}
                                  </span>
                                </TableCell>
                                <TableCell className="text-text-primary text-sm">
                                  {testCase.owner_name || testCase.owner || "-"}
                                </TableCell>
                                <TableCell className="text-text-primary text-sm">
                                  {testCase.tags &&
                                  Array.isArray(testCase.tags) &&
                                  testCase.tags.length > 0
                                    ? testCase.tags.join(", ")
                                    : "-"}
                                </TableCell>
                                <TableCell className="text-text-primary text-sm">
                                  {testCase.estimated_time
                                    ? `${testCase.estimated_time}`
                                    : "--"}
                                </TableCell>
                              </TableRow>
                            ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </div>

              {/* Right Panel - Filters */}
              {showFilters && (
                <Card className="md:col-span-3 p-4 flex flex-col min-h-0 h-full max-h-[70vh] md:max-h-none overflow-hidden">
                  <div className="shrink-0 flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-text-primary">Filters</h3>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-[#F0F5FEFF] text-[#2E70F5FF] hover:bg-[#E4EDFEFF] hover:text-[#2E70F5FF]"
                      onClick={handleApplyFilters}
                    >
                      Apply
                    </Button>
                  </div>

                  <div className="mb-4 flex min-h-10 shrink-0 flex-row flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-input bg-background px-3">
                    <div className="flex h-10 min-h-10 items-center gap-2.5">
                      <input
                        type="radio"
                        id="match-all-specific"
                        name="filter-logic-specific"
                        value="all"
                        checked={filterLogic === "all"}
                        onChange={(e) => setFilterLogic(e.target.value)}
                        className="h-5 w-5 shrink-0 cursor-pointer"
                        style={{ accentColor: "#a17561ff" }}
                      />
                      <Label
                        htmlFor="match-all-specific"
                        className="cursor-pointer text-base font-normal leading-none text-text-primary"
                      >
                        Match all filters
                      </Label>
                    </div>
                    <div className="flex h-10 min-h-10 items-center gap-2.5">
                      <input
                        type="radio"
                        id="match-any-specific"
                        name="filter-logic-specific"
                        value="any"
                        checked={filterLogic === "any"}
                        onChange={(e) => setFilterLogic(e.target.value)}
                        className="h-5 w-5 shrink-0 cursor-pointer"
                        style={{ accentColor: "#a17561ff" }}
                      />
                      <Label
                        htmlFor="match-any-specific"
                        className="cursor-pointer text-base font-normal leading-none text-text-primary"
                      >
                        Match any one filters
                      </Label>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">
                          Select specific folder
                        </Label>
                        <Select
                          value={filters.suite || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "suite",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="0 Folder selected" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Folders</SelectItem>
                            {sections.map((section) => {
                              const sectionId =
                                section.section_id || section.folder_id;
                              return (
                                <SelectItem key={sectionId} value={sectionId}>
                                  {section.path || section.name}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Status</Label>
                        <Select
                          value={filters.status || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "status",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="Select Options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Priority</Label>
                        <Select
                          value={filters.priority || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "priority",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="Select Options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Type Of Test Cases</Label>
                        <Select
                          value={filters.type || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "type",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="Select Options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="functional">
                              Functional
                            </SelectItem>
                            <SelectItem value="integration">
                              Integration
                            </SelectItem>
                            <SelectItem value="performance">
                              Performance
                            </SelectItem>
                            <SelectItem value="security">Security</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Automation Type</Label>
                        <Select
                          value={filters.automation_type || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "automation_type",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="Select Options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="not_automated">
                              Not Automated
                            </SelectItem>
                            <SelectItem value="automated">Automated</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Automation Status</Label>
                        <Select
                          value={filters.automation_status || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "automation_status",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="Select Options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="ready">Ready</SelectItem>
                            <SelectItem value="in_progress">
                              In Progress
                            </SelectItem>
                            <SelectItem value="blocked">Blocked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Label</Label>
                        <Select
                          value={filters.label || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "label",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="Select Options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            {/* TODO: Load labels from API */}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Owner</Label>
                        <Select
                          value={filters.owner || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(
                              "owner",
                              value === "all" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue placeholder="Select Options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            {/* TODO: Load owners from API */}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs">Reference</Label>
                        <Input
                          value={filters.references || ""}
                          onChange={(e) =>
                            handleFilterChange("references", e.target.value)
                          }
                          placeholder="Select Options"
                          className="h-8 text-xs mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectTestCasesModal;
